import z from 'zod'
import { ErrorBox } from '@/components/commons/error-box'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { AlertTriangleIcon } from '@/components/ui/icons'
import { AcceptSubmissionDialog } from '@/features/luxe-submissions/components/accept-submission-dialog'
import { ListingDetailsHeaderBase } from '@/features/luxe-submissions/components/listing-details-header-base'
import {
	type ListingDetailsField,
	ListingDetailsInfoBase
} from '@/features/luxe-submissions/components/listing-details-info-base'
import { ListingDetailsLoading } from '@/features/luxe-submissions/components/listing-details-loading'
import { ListingDetailsPhoto } from '@/features/luxe-submissions/components/listing-details-photo'
import { RejectSubmissionDialog } from '@/features/luxe-submissions/components/reject-submission-dialog'
import { useLuxeFeatureFlag } from '@/features/luxe-submissions/queries/use-get-luxe-feature-flags'
import { useGetQueueListingDetails } from '@/features/luxe-submissions/queries/use-get-queue-listing-details'
import {
	essentialAmenityToLabelTKey,
	propertyTypeToLabelTKey
} from '@/features/luxe-submissions/utils'
import { raise } from '@/helpers/utils'
import { useURLParsedParams } from '@/hooks/use-url-parsed-params'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

function LuxeSubmissionsListing() {
	const { t } = useTranslation()

	const urlParams = useURLParsedParams(
		z.object({
			id: z.string()
		})
	)

	const listingId = urlParams.success
		? String(urlParams.data.id)
		: raise('Invalid listing ID in URL parameters')

	const { data, isLoading, isError } = useGetQueueListingDetails({
		listingId
	})

	const { isEnabled: isVendorPrecheckEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-vendor-precheck'
	)

	if (isLoading) {
		return <ListingDetailsLoading />
	}
	if (!data || isError) {
		return (
			<ErrorBox
				icon={<AlertTriangleIcon />}
				title={t('errors:default')}
			/>
		)
	}

	// Null both with the flag off and when the submission skipped the landing form.
	const gatingAnswers = isVendorPrecheckEnabled ? data.gatingAnswers : null

	const attestedAmenities =
		gatingAnswers?.amenitySelfAttestation
			.filter((amenity) => amenity.attested)
			.map((amenity) => t(essentialAmenityToLabelTKey[amenity.key])) ?? []

	// Labels are stored lowercase so they read as a sentence once joined; only
	// the first one is capitalised, whichever amenity happens to come first.
	const joinedAmenities = attestedAmenities.join(', ')
	const attestedAmenitiesLabel =
		joinedAmenities.charAt(0).toUpperCase() + joinedAmenities.slice(1)

	const fields: ListingDetailsField[] = [
		{
			label: t('common:email'),
			value: data.hostEmail
		},
		{
			label: t('common:location'),
			value: data.listingCity
		},
		{
			label: t('luxe_submissions:property_address_column'),
			value: data.propertyAddress
		},
		{
			label: t('luxe_submissions:property_type_column'),
			value: data.propertyType
				? t(propertyTypeToLabelTKey[data.propertyType])
				: '-'
		},
		{
			label: t('luxe_submissions:photos_count'),
			value: data.gallery?.images.length ?? 0
		},
		{
			label: t('luxe_submissions:description'),
			value: data.description ?? '-'
		},
		{
			label: t('luxe_submissions:property_link'),
			value: data.propertyLink ? (
				<a
					className='break-all hover:underline'
					href={data.propertyLink}
					rel='noopener noreferrer'
					target='_blank'
				>
					{data.propertyLink}
				</a>
			) : (
				'-'
			)
		},
		// The host's own landing-form answers, recorded verbatim: the backend
		// applies no pass/fail logic, so they are shown exactly as submitted.
		...(gatingAnswers
			? [
					{
						label: t('luxe_submissions:gating_bedroom_count'),
						value: gatingAnswers.bedroomCount
					},
					{
						label: t('luxe_submissions:gating_bathroom_count'),
						value: gatingAnswers.bathroomCount
					},
					{
						label: t('luxe_submissions:gating_max_occupancy'),
						value: gatingAnswers.maxOccupancy
					},
					{
						label: t('luxe_submissions:essential_amenities'),
						value: attestedAmenitiesLabel || '-'
					}
				]
			: [])
	]

	const showActions =
		data.precheckStatus === 'pending' && data.precheckState !== 'ml_review'
	return (
		<div className='flex flex-1 flex-col overflow-hidden'>
			<ListingDetailsHeaderBase
				actions={
					showActions ? (
						<>
							<RejectSubmissionDialog
								listingId={data.id}
								trigger={
									<ButtonLegacy
										type='button'
										variant='outline'
									>
										{t('common:reject')}
									</ButtonLegacy>
								}
							/>

							<AcceptSubmissionDialog
								listingId={data.id}
								trigger={
									<ButtonLegacy type='button' variant='brand'>
										{t('common:accept')}
									</ButtonLegacy>
								}
							/>
						</>
					) : undefined
				}
				backUrl={
					routes.LUXE_SUBMISSIONS_INITIAL_EVALUATIONS.handle.fullPath
				}
				listingId={data.id}
				listingLink={data.propertyLink}
				location={data.listingCity}
				source={data.source ?? undefined}
				title={t('luxe_submissions:initial_evaluation_for')}
			/>

			<div className='grid h-full grid-cols-[2fr_1fr] overflow-hidden pl-4'>
				<ListingDetailsPhoto photos={data.gallery?.images ?? []} />
				<ListingDetailsInfoBase fields={fields} />
			</div>
		</div>
	)
}

export { LuxeSubmissionsListing }
